import { CloseFullscreenRounded, OpenInFullRounded } from "@mui/icons-material";
import { Box, Card, CardHeader, Divider, IconButton, Stack, SxProps, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { TeamOverviewSection } from "src/constants";
import { useBreakpoints, useSettings } from "src/hooks";
import { TextViewer, isWhitespace } from "../../../../components";
import { useTeamPageLayoutContext } from "../../TeamPageLayout";

interface IProps {
  sx?: SxProps;
}

const TeamAbout = (props: IProps) => {
  const { team } = useTeamPageLayoutContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { teamOverviewSettings, updateTeamOverviewSettings } = useSettings();
  const { isMdUp } = useBreakpoints();

  useEffect(() => {
    videoRef.current?.load();
  }, [team.videoUrl]);

  return (
    <Card sx={{ ...props.sx }}>
      <CardHeader
        title={
          <Stack direction="row" spacing={0.5} sx={{
            alignItems: "center"
          }}>
            <Typography variant="h6">About</Typography>
            {isMdUp && (
              <IconButton
                variant="tertiary"
                onClick={() => {
                  updateTeamOverviewSettings({
                    expandedSection:
                      teamOverviewSettings.expandedSection === TeamOverviewSection.About
                        ? undefined
                        : TeamOverviewSection.About,
                  });
                  window.scrollTo({ top: 0 });
                }}
              >
                {teamOverviewSettings.expandedSection === TeamOverviewSection.About ? (
                  <CloseFullscreenRounded />
                ) : (
                  <OpenInFullRounded />
                )}
              </IconButton>
            )}
          </Stack>
        }
      />
      <Divider />
      <Stack
        spacing={{ xs: 0.5, sm: 1 }}
        sx={{
          overflow: "auto",
          p: 1
        }}>
        <Box>
          <Typography variant="overline">Purpose</Typography>
          <Typography>{team.description}</Typography>
        </Box>

        {team.hasVideo && (
          <Stack spacing={0.5}>
            <Typography variant="overline">Team Video</Typography>
            <video ref={videoRef} controls width="100%" style={{ borderRadius: 8 }}>
              <source src={team.videoUrl} />
            </video>
          </Stack>
        )}

        {team.accountabilities && !isWhitespace(team.accountabilities) && (
          <Box>
            <Typography variant="overline">Accountabilities</Typography>
            <TextViewer value={team.accountabilities} />
          </Box>
        )}

        {team.boundaries && !isWhitespace(team.boundaries) && (
          <Box>
            <Typography variant="overline">Boundaries</Typography>
            <TextViewer value={team.boundaries} />
          </Box>
        )}

        {team.domains && !isWhitespace(team.domains) && (
          <Box>
            <Typography variant="overline">Domains</Typography>
            <TextViewer value={team.domains} />
          </Box>
        )}

        {team.communicationChannels && !isWhitespace(team.communicationChannels) && (
          <Box>
            <Typography variant="overline">Communication Channels</Typography>
            <TextViewer value={team.communicationChannels} />
          </Box>
        )}

        {team.otherInformation && !isWhitespace(team.otherInformation) && (
          <Box>
            <Typography variant="overline">Other Information</Typography>
            <TextViewer value={team.otherInformation} />
          </Box>
        )}
      </Stack>
    </Card>
  );
};

export { TeamAbout };
